import React, { useEffect, useRef, useState } from 'react';
import { ScanLine, Loader2, X, AlertTriangle, Check, FlaskConical, FileWarning, Receipt } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { uploads, serviceReports, apiCustomers } from '../../lib/api';
import CustomerCombobox from '../forms/CustomerCombobox';
import { toast } from 'sonner';

const money = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// How many sheets go over in one go. The server holds the same ceiling.
const BATCH_CAP = 20;

// The screen between a stack of paper service reports and the books.
//
// The charge for the visit is the only figure that becomes an invoice. The
// previous balance and total due are shown beside it for checking against the
// paper and are never billed — the total due already contains invoices this
// customer has been sent, and it is the biggest number on the page.
//
// A photograph is a sheet. Several photographs are several sheets, unless the
// office says they are pages of one report, which is the other thing a stack of
// four images can mean. Nothing is written until someone has looked.
export default function ReportScanner({ onClose, onFiled }) {
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState('');
  const [pending, setPending] = useState(null); // files chosen, waiting on "one report or many?"
  const [preview, setPreview] = useState([]);
  const [attachmentIds, setAttachmentIds] = useState([]);
  const [scan, setScan] = useState(null);
  const [form, setForm] = useState(null);
  const [batch, setBatch] = useState(null); // [{ attachmentId, preview, scan, form, result }]
  const [filedAny, setFiledAny] = useState(false);
  const previews = useRef([]);

  useEffect(() => { apiCustomers.list().then((c) => setCustomers(c || [])).catch(() => {}); }, []);
  useEffect(() => () => { previews.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const formFor = (proposal) => {
    const r = proposal.report || {};
    return {
      customerId: proposal.customer?.id || '',
      serviceDate: r.serviceDate || '',
      serviceType: r.serviceType || 'Regular',
      technician: r.technician || '',
      notes: r.notes || '',
      amount: r.thisService ?? '',
      createInvoice: true,
      products: (proposal.pesticideApplications || []).map((p) => ({ ...p })),
    };
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []).slice(0, BATCH_CAP);
    e.target.value = '';
    if (!files.length) return;
    setScan(null); setForm(null); setBatch(null);
    // One photograph can only be one report, so there is nothing to ask.
    if (files.length === 1) { run(files, 'single'); return; }
    setPending(files);
  };

  const run = async (files, mode) => {
    setPending(null);
    const urls = files.map((f) => { const u = URL.createObjectURL(f); previews.current.push(u); return u; });
    setPreview(urls); setBusy('upload');
    try {
      const res = await uploads.upload(files, { kind: 'servicereport', linkedType: 'workorder', note: 'service report scan' });
      const ids = (res?.files || []).map((f) => f.id);
      setAttachmentIds(ids);
      setBusy('read');
      if (mode === 'single') {
        const proposal = await serviceReports.scan(ids);
        setScan(proposal);
        setForm(formFor(proposal));
      } else {
        const res2 = await serviceReports.scanBatch(ids);
        setBatch((res2.reports || []).map((proposal, i) => ({
          attachmentId: ids[i],
          preview: urls[i],
          scan: proposal,
          form: proposal.error ? null : formFor(proposal),
          result: proposal.error ? { kind: 'error', text: proposal.error } : null,
        })));
        if (res2.failedCount) toast.warning(`${res2.failedCount} of ${ids.length} could not be read`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read that report');
      setPreview([]);
    } finally { setBusy(''); }
  };

  const bodyFor = (ids, f) => ({
    attachmentIds: ids,
    customerId: f.customerId,
    serviceDate: f.serviceDate,
    serviceType: f.serviceType,
    technician: f.technician,
    notes: f.notes,
    amount: Number(f.amount) || 0,
    createInvoice: f.createInvoice,
    products: f.products.map((p) => ({
      name: p.name, epaRegNo: p.epaRegNo || '', site: p.site || '',
      method: p.method || '', equipment: p.equipment || '',
      amountUsed: p.amountUsed || '', dilutionPct: p.dilutionPct || '',
    })),
  });

  // What the server did with the billing, in the office's words.
  const outcome = (res) => {
    if (res.alreadyFiled) return { kind: 'skipped', text: 'Already filed — nothing changed' };
    if (res.invoiceAction === 'draft-sent') return { kind: 'sent', text: `Draft invoice ${res.invoice?.number} marked sent` };
    if (res.invoiceAction === 'existing') return { kind: 'skipped', text: `Invoice ${res.invoice?.number} already covered this date` };
    if (res.invoiceAction === 'created') return { kind: 'filed', text: `Filed · invoice ${res.invoice?.number}` };
    return { kind: 'filed', text: 'Filed · no invoice' };
  };

  const file = async () => {
    setBusy('file');
    try {
      const res = await serviceReports.apply(bodyFor(attachmentIds, form));
      toast.success(outcome(res).text);
      onFiled && onFiled();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not file that report — nothing was changed');
    } finally { setBusy(''); }
  };

  // Filed one at a time so a sheet that fails leaves the rest of the stack alone.
  const fileAll = async () => {
    setBusy('file');
    let done = 0;
    for (let i = 0; i < batch.length; i++) {
      const row = batch[i];
      if (!row.form || row.result?.kind === 'filed' || row.result?.kind === 'sent' || row.result?.kind === 'skipped') continue;
      if (!row.form.customerId || !row.form.serviceDate) {
        setBatch((b) => b.map((x, j) => j === i ? { ...x, result: { kind: 'error', text: 'Needs a customer and a service date' } } : x));
        continue;
      }
      try {
        const res = await serviceReports.apply(bodyFor([row.attachmentId], row.form));
        const out = outcome(res);
        done += 1;
        setBatch((b) => b.map((x, j) => j === i ? { ...x, result: out } : x));
      } catch (e) {
        const text = e?.response?.data?.detail || 'Could not file this one — nothing was changed';
        setBatch((b) => b.map((x, j) => j === i ? { ...x, result: { kind: 'error', text } } : x));
      }
    }
    setBusy('');
    setFiledAny(true);
    if (done) { toast.success(`${done} report${done === 1 ? '' : 's'} filed`); onFiled && onFiled(); }
  };

  const setRow = (i, patch) => setBatch((b) => b.map((x, j) => j === i ? { ...x, form: { ...x.form, ...patch } } : x));

  const r = scan?.report;
  const canFile = form && form.customerId && form.serviceDate && Number(form.amount) >= 0 && !busy;
  const readyRows = batch ? batch.filter((x) => x.form && !x.result).length : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="report-scanner-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><ScanLine className="w-5 h-5 text-[#8BC53F]" />Scan service reports</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="inline-flex items-center gap-1.5 px-4 h-10 bg-white border border-slate-200 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer">
              {busy === 'upload' || busy === 'read' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
              {busy === 'read' ? 'Reading the reports…' : busy === 'upload' ? 'Uploading…' : 'Choose or photograph reports'}
              <input type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={onPick} data-testid="report-file-input" />
            </label>
            <p className="text-xs text-slate-500 mt-2">One photograph per report, up to {BATCH_CAP} at a time. JPEG or PNG.</p>
            {preview.length > 0 && !batch && (
              <div className="flex gap-2 mt-3 flex-wrap">
                {preview.map((u, i) => <img key={i} src={u} alt="Report scan" className="h-28 rounded border border-slate-200" />)}
              </div>
            )}
          </div>

          {/* Four photographs can be four reports or one four-page report, and
              only the person holding the paper knows which. */}
          {pending && (
            <Card className="p-4 border-slate-200 bg-slate-50" data-testid="report-mode-choice">
              <div className="text-sm font-bold text-slate-900">{pending.length} photographs — what are they?</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button onClick={() => run(pending, 'batch')} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="report-mode-batch">
                  {pending.length} separate reports
                </Button>
                <Button variant="outline" onClick={() => run(pending.slice(0, 4), 'single')} disabled={pending.length > 4} data-testid="report-mode-single">
                  Pages of one report
                </Button>
                <Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
              </div>
              {pending.length > 4 && <p className="text-xs text-slate-500 mt-2">One report can be at most 4 pages.</p>}
            </Card>
          )}

          {/* ---------------------------------------------------- a stack */}
          {batch && (
            <div className="space-y-3" data-testid="report-batch">
              {batch.map((row, i) => {
                const held = row.scan?.existingInvoice;
                return (
                  <Card key={row.attachmentId} className={`p-4 ${row.result?.kind === 'error' ? 'border-red-300' : row.result ? 'border-slate-200 bg-slate-50' : 'border-slate-200'}`}>
                    <div className="flex gap-3">
                      <img src={row.preview} alt="Report scan" className="h-20 w-16 object-cover rounded border border-slate-200" />
                      <div className="flex-1 min-w-0">
                        {row.form ? (
                          <>
                            <div className="grid sm:grid-cols-2 gap-2">
                              <div className="sm:col-span-2">
                                <CustomerCombobox customers={customers} value={row.form.customerId}
                                  onSelect={(c) => setRow(i, { customerId: c.id })} placeholder="Search customers…" />
                              </div>
                              <Input type="date" value={row.form.serviceDate}
                                onChange={(e) => setRow(i, { serviceDate: e.target.value })} className="h-9" data-testid={`report-date-${i}`} />
                              <Input type="number" step="0.01" value={row.form.amount}
                                onChange={(e) => setRow(i, { amount: e.target.value })} className="h-9" data-testid={`report-amount-${i}`} />
                            </div>
                            <label className="flex items-center gap-2 mt-2 text-xs text-slate-700">
                              <input type="checkbox" checked={row.form.createInvoice}
                                onChange={(e) => setRow(i, { createInvoice: e.target.checked })} />
                              Invoice {money(row.form.amount)}
                            </label>
                          </>
                        ) : (
                          <div className="text-sm text-red-700 flex items-center gap-1.5"><FileWarning className="w-4 h-4" />This page could not be read.</div>
                        )}

                        {held && (
                          <div className="mt-2 text-xs text-amber-900 flex items-start gap-1.5" data-testid={`report-held-${i}`}>
                            <Receipt className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>Invoice {held.number} ({held.status}) already covers this date.
                              {held.status.toLowerCase() === 'draft' ? ' Filing will mark it sent.' : ' No second invoice will be written.'}</span>
                          </div>
                        )}
                        {row.scan?.notes?.length > 0 && !row.result && (
                          <ul className="mt-2 space-y-0.5 text-[11px] text-amber-900 list-disc pl-4">
                            {row.scan.notes.slice(0, 3).map((n, k) => <li key={k}>{n}</li>)}
                          </ul>
                        )}
                        {row.result && (
                          <div className={`mt-2 text-xs font-semibold flex items-center gap-1.5 ${
                            row.result.kind === 'error' ? 'text-red-700' : row.result.kind === 'skipped' ? 'text-slate-600' : 'text-[#6FA82C]'}`}
                            data-testid={`report-result-${i}`}>
                            {row.result.kind === 'error' ? <AlertTriangle className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                            {row.result.text}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}

              <div className="flex gap-2 pt-1">
                <Button onClick={fileAll} disabled={!readyRows || !!busy} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="report-file-all-btn">
                  {busy === 'file' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  File {readyRows} report{readyRows === 1 ? '' : 's'}
                </Button>
                <Button variant="outline" onClick={onClose}>{filedAny ? 'Done' : 'Cancel'}</Button>
              </div>
            </div>
          )}

          {/* ------------------------------------------------ one report */}
          {form && (
            <>
              {scan?.notes?.length > 0 && (
                <Card className={`p-4 ${scan.needsReview ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`} data-testid="report-scan-notes">
                  <div className="text-xs font-bold flex items-center gap-1.5 text-amber-900">
                    <AlertTriangle className="w-3.5 h-3.5" />Check these against the paper
                  </div>
                  <ul className="mt-1.5 space-y-1 text-xs text-amber-900 list-disc pl-5">
                    {scan.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </Card>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Customer</Label>
                  <CustomerCombobox customers={customers} value={form.customerId}
                    onSelect={(c) => setForm({ ...form, customerId: c.id })} placeholder="Search customers…" />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {scan?.matchReason ? `Matched because ${scan.matchReason}.` : ''}
                    {scan?.matchConfidence === 'low' ? ' Worth confirming.' : ''}
                  </p>
                </div>
                <div><Label className="text-xs">Service date</Label>
                  <Input type="date" value={form.serviceDate} onChange={(e) => setForm({ ...form, serviceDate: e.target.value })} className="mt-1" data-testid="report-date" /></div>
                <div><Label className="text-xs">Type of service</Label>
                  <Input value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Technician</Label>
                  <Input value={form.technician} onChange={(e) => setForm({ ...form, technician: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Charge for this service</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" data-testid="report-amount" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Technician notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
              </div>

              {scan?.existingInvoice && (
                <Card className="p-4 border-amber-300 bg-amber-50" data-testid="report-existing-invoice">
                  <div className="text-xs font-bold text-amber-900 flex items-center gap-1.5">
                    <Receipt className="w-3.5 h-3.5" />
                    Invoice {scan.existingInvoice.number} ({scan.existingInvoice.status}) already covers this service date
                  </div>
                  <p className="text-xs text-amber-900 mt-1">
                    {scan.existingInvoice.status.toLowerCase() === 'draft'
                      ? 'Filing this report will mark that draft sent rather than write a second invoice.'
                      : 'Filing this report will record the service but will not write a second invoice.'}
                  </p>
                </Card>
              )}

              <Card className="p-4 border-slate-200 bg-slate-50">
                <div className="text-xs font-bold text-slate-700">Billing block as written on the form</div>
                <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                  <div><div className="text-lg font-extrabold tabular-nums text-slate-900">{money(r?.thisService)}</div><div className="text-xs text-slate-500">This service</div></div>
                  <div><div className="text-lg font-extrabold tabular-nums text-slate-600">{money(r?.previousBalance)}</div><div className="text-xs text-slate-500">Previous balance</div></div>
                  <div><div className="text-lg font-extrabold tabular-nums text-slate-600">{money(r?.totalDue)}</div><div className="text-xs text-slate-500">Total due</div></div>
                </div>
                <p className="text-xs text-slate-600 mt-3">
                  Only the charge for this service is invoiced. The previous balance is already on invoices
                  this customer has been sent{scan?.invoice?.previousBalanceInApp !== undefined
                    ? ` — the app currently holds ${money(scan.invoice.previousBalanceInApp)} outstanding for them` : ''}.
                </p>
                <label className="flex items-center gap-2 mt-3 text-sm text-slate-700">
                  <input type="checkbox" checked={form.createInvoice} onChange={(e) => setForm({ ...form, createInvoice: e.target.checked })} />
                  Create an invoice for {money(form.amount)}
                </label>
              </Card>

              {form.products.length > 0 && (
                <div>
                  <h4 className="font-bold text-slate-900 text-sm flex items-center gap-1.5"><FlaskConical className="w-4 h-4 text-[#8BC53F]" />Pesticide applications to file</h4>
                  <table className="w-full mt-2 text-sm">
                    <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                      <th className="py-1.5">Product</th><th>EPA #</th><th>Site</th><th>Method</th><th className="w-28">Amount</th>
                    </tr></thead>
                    <tbody>
                      {form.products.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 font-semibold text-slate-800">{p.name}</td>
                          <td className="text-xs text-slate-600 tabular-nums">{p.epaRegNo || '—'}</td>
                          <td className="text-xs text-slate-600">{p.siteLabel || p.site || '—'}</td>
                          <td className="text-xs text-slate-600">{p.methodLabel || p.method || '—'}</td>
                          <td>
                            <Input value={p.amountUsed || ''} placeholder="e.g. 1 gal"
                              onChange={(e) => setForm({ ...form, products: form.products.map((x, j) => j === i ? { ...x, amountUsed: e.target.value } : x) })}
                              className="h-8 text-xs" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-500 mt-2">
                    Amounts are stored as written. These feed the DPR monthly summary, so an amount left blank
                    is a gap in that report.
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button onClick={file} disabled={!canFile} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="report-file-btn">
                  {busy === 'file' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}File this report
                </Button>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
